import { alpha, Palette, Theme } from '@mui/material/styles';
import { FontSize } from '@store/types/UserStoreSlice.type';
import colors from '@theme/colors';
import { isDarkPagePalette } from '@theme/palettes/getPalette';
import { getTypographyVariants } from '@theme/styleOverrides/typography';

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    filledBorder: true;
  }
}

export const MuiChip = ({ palette, fontSize }: { palette: Palette; fontSize: FontSize }) => ({
  styleOverrides: {
    root: {
      '& .MuiChip-label': {
        ...getTypographyVariants(fontSize).root,
        display: 'flex',
      },
    },
  },
  variants: [
    {
      props: {},
      style: {
        paddingLeft: '4px',
        paddingRight: '4px',
      },
    },
    {
      props: { variant: 'outlined' },
      style: {
        color: palette.theme === 'lio' ? colors.FADED_NAVY : palette.text.main,
        backgroundColor: palette.theme === 'lio' ? palette.common.white : palette.background.paper,
        borderColor: 'rgb(189, 189, 189)', // TODO: find in theme
        // MUI tints the delete x with primary.main, which is the page navy on dark page palettes
        ...(isDarkPagePalette(palette) && {
          '& .MuiChip-deleteIcon': {
            color: alpha(palette.text.main, 0.7),
            '&:hover, &:active': { color: palette.text.main },
          },
        }),
      },
    },
    {
      props: { variant: 'filled' },
      style: {
        color: colors.WHITE_100,
        // a translucent lift reads as a pill on any dark surface; the inset color sat darker than the card
        backgroundColor: palette.theme === 'lio' ? colors.FADED_NAVY : alpha(palette.common.white, 0.12),
        '&:hover': {
          backgroundColor: palette.theme === 'lio' ? colors.FADED_NAVY : alpha(palette.common.white, 0.18),
        },

        '&:focus-visible': {
          backgroundColor: 'rgb(189, 189, 189)',
        },
      },
    },
    {
      props: { variant: 'filledBorder' },
      style: {
        color: palette.theme === 'lio' ? palette.common.white : palette.background.main,
        backgroundColor: palette.theme === 'lio' ? colors.FADED_NAVY : palette.background.main,
        border: `thin solid #bdbdbd`, // TODO: is there a color we can use? this doenst exist but maybe one close does
      },
    },
    // secondary styles for the darker blue/coral chips found on home page
    {
      props: { color: 'secondary', variant: 'filled' },
      style: {
        color: colors.WHITE_100,
        backgroundColor: colors.CORAL,
        borderColor: colors.CORAL,
        '&:hover': {
          backgroundColor: colors.CORAL,
        },

        '&:focus-visible': {
          backgroundColor: colors.CORAL,
          outline: '-webkit-focus-ring-color auto 1px',
        },
      },
    },
    {
      props: { color: 'secondary', variant: 'outlined' },
      style: {
        color: colors.WHITE_100,
        backgroundColor: '#465077', // TODO could this be in theme/is there a similar color to use

        border: 'none',
        '&:hover': {
          backgroundColor: '#465077 !important', // something in muibutton is overriding this specific style
        },

        '&:focus-visible': {
          backgroundColor: '#465077',
          outline: '-webkit-focus-ring-color auto 1px',
        },
      },
    },
  ],
});

const onDarkPage = (theme: Theme, value: string) => (isDarkPagePalette(theme.palette) ? value : undefined);

// a selected filter pill: the filled chip is darker than the card on dark page palettes, which reads as unselected,
// so show it light there instead (no change on LIO)
export const selectedChipSx = {
  color: (theme: Theme) => onDarkPage(theme, theme.palette.primary.main),
  backgroundColor: (theme: Theme) => onDarkPage(theme, theme.palette.common.white),
  '&:hover': {
    backgroundColor: (theme: Theme) => onDarkPage(theme, alpha(theme.palette.common.white, 0.85)),
  },
};
